import { useCallback, useEffect, useRef, useState } from "react";
import { X, Loader2, Trash2, RefreshCw, Plus, Upload, Send, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { STATUS_LABEL, STATUS_CLS } from "./constants";

const MISSION_STATUS = { a_faire: "À faire", en_cours: "En cours", livree: "Livrée", validee: "Validée" };
const LEAD_STATUS = ["nouveau", "qualifie", "gagne", "perdu"];
const SUPPLIER_STATUS = { confirmed: "Confirmée", shipped: "Expédiée", delivered: "Livrée" };

/** Fiche partenaire (admin) : accès par module, statut, et onglets métier selon le type. */
export function PartnerDetail({ partner, api, registry, onClose, onSaved, onDeleted }) {
  const modules = registry?.partner_modules?.[partner.type] || [];
  const typeLabel = registry?.partner_types?.find((t) => t.key === partner.type)?.label || partner.type;
  const tabs = tabsFor(partner.type, partner.access);
  const [tab, setTab] = useState(tabs[0]?.k || "acces");
  const [busy, setBusy] = useState(false);

  const toggle = async (key, v) => {
    setBusy(true);
    try {
      const r = await api.put(`/partners/${partner.partner_id}`, { access_overrides: { ...(partner.access_overrides || {}), [key]: v } });
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const setStatus = async (status) => {
    try { const r = await api.put(`/partners/${partner.partner_id}`, { status }); onSaved(r.data); toast.success("Statut mis à jour"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const resend = async () => {
    try { const r = await api.post(`/partners/${partner.partner_id}/resend-invite`); await navigator.clipboard.writeText(r.data.invite_url).catch(() => {}); toast.success(r.data.email_sent ? "Invitation renvoyée par email (lien copié)" : "Nouveau lien copié dans le presse-papiers"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer ce partenaire et désactiver son accès ?")) return;
    try { await api.delete(`/partners/${partner.partner_id}`); onDeleted(partner.partner_id); toast.success("Partenaire supprimé"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-2xl overflow-hidden" data-testid="partner-detail">
      <div className="px-5 py-4 border-b border-white/10 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-black text-white" data-testid="partner-detail-name">{partner.company || partner.name}</span>
            <span className={"text-[9px] px-1.5 py-0.5 rounded uppercase font-bold " + (STATUS_CLS[partner.status] || "")}>{STATUS_LABEL[partner.status] || partner.status}</span>
            <span className="text-[10px] text-sky-300 font-bold uppercase">{typeLabel}</span>
          </div>
          <div className="text-xs text-slate-400">{partner.name} · {partner.email}{partner.phone ? ` · ${partner.phone}` : ""} · {partner.societe_name}</div>
        </div>
        <div className="flex gap-1">
          {partner.status === "invited" && <Button size="sm" variant="outline" onClick={resend} className="border-white/10 text-slate-200 text-xs" data-testid="partner-resend"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Renvoyer</Button>}
          {partner.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus("suspended")} className="border-red-500/30 text-red-300 text-xs" data-testid="partner-suspend">Suspendre</Button>}
          {partner.status === "suspended" && <Button size="sm" variant="outline" onClick={() => setStatus("active")} className="border-emerald-500/30 text-emerald-300 text-xs" data-testid="partner-reactivate">Réactiver</Button>}
          <Button size="sm" variant="ghost" onClick={remove} className="text-red-400" data-testid="partner-delete"><Trash2 className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-400" data-testid="partner-detail-close"><X className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 px-3 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={"partner-tab-" + t.k}
            className={"px-3 py-2.5 text-xs font-bold border-b-2 whitespace-nowrap " + (tab === t.k ? "border-[#f7c935] text-[#f7c935]" : "border-transparent text-slate-400 hover:text-white")}>{t.label}</button>
        ))}
      </div>

      <div className="p-5">
        {tab === "acces" && (
          <div className="space-y-2" data-testid="partner-access-panel">
            <p className="text-[11px] text-slate-500 mb-3">Modules actifs pour ce partenaire (surcharge les défauts du Builder de la société).</p>
            {modules.map((m) => (
              <label key={m.key} className="flex items-center justify-between gap-3 bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2.5 text-xs cursor-pointer">
                <div><span className="text-slate-100 font-semibold">{m.label}</span><span className="text-slate-500 ml-2">{m.desc}</span></div>
                <Switch disabled={busy} checked={!!partner.access?.[m.key]} onCheckedChange={(v) => toggle(m.key, v)} data-testid={"partner-access-" + m.key} />
              </label>
            ))}
          </div>
        )}
        {tab === "commandes" && <OrdersTab api={api} partner={partner} />}
        {tab === "missions" && <MissionsTab api={api} partner={partner} />}
        {tab === "leads" && <LeadsTab api={api} partner={partner} />}
        {tab === "messages" && <MessagesTab api={api} partner={partner} />}
        {tab === "documents" && <DocumentsTab api={api} partner={partner} />}
      </div>
    </div>
  );
}

function tabsFor(type, access) {
  const t = [{ k: "acces", label: "Accès" }];
  if (type === "fournisseur") t.push({ k: "commandes", label: "Commandes" });
  if (type === "sous_traitant") t.push({ k: "missions", label: "Missions" });
  if (type === "apporteur") t.push({ k: "leads", label: "Leads & commissions" });
  t.push({ k: "messages", label: "Messagerie" }, { k: "documents", label: "Documents" });
  return t;
}

function useList(api, url, deps = []) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(url).then((r) => setItems(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch((e) => toast.error(e.response?.data?.detail || e.message)), [api, url]);
  // eslint-disable-next-line
  useEffect(() => { load(); }, deps);
  return [items, load, setItems];
}

function OrdersTab({ api, partner }) {
  const [orders] = useList(api, `/partners/${partner.partner_id}/orders`, [partner.partner_id]);
  if (!orders) return <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" />;
  if (!orders.length) return <Empty text="Aucun bon de commande pour ce fournisseur. Créez-en depuis Inventaire → Commandes fournisseurs." />;
  return (
    <div className="space-y-2" data-testid="partner-orders-admin">
      {orders.map((o) => (
        <div key={o.po_id} className="bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-mono text-slate-300">{o.po_id}</span>
            <span className="text-slate-400">{o.total_ht_eur?.toLocaleString("fr-FR")} € HT</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 uppercase">{o.status}</span>
            <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (o.supplier_status ? "bg-sky-500/15 text-sky-300" : "bg-white/5 text-slate-500")}>{SUPPLIER_STATUS[o.supplier_status] || "En attente fournisseur"}</span>
          </div>
          {(o.supplier_tracking || o.supplier_invoice) && <div className="text-[11px] text-slate-500 mt-1">{o.supplier_tracking && <>Suivi : <span className="text-slate-300">{o.supplier_tracking}</span> · </>}{o.supplier_invoice && <>Facture : <span className="text-emerald-300">{o.supplier_invoice.filename}</span></>}</div>}
        </div>
      ))}
    </div>
  );
}

function MissionsTab({ api, partner }) {
  const [missions, reload] = useList(api, `/partners/${partner.partner_id}/missions`, [partner.partner_id]);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", amount_ht: "" });
  const create = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    try { await api.post(`/partners/${partner.partner_id}/missions`, form); setForm({ title: "", description: "", due_date: "", amount_ht: "" }); reload(); toast.success("Mission créée"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const setStatus = async (m, status) => { try { await api.put(`/partners/${partner.partner_id}/missions/${m.mission_id}`, { status }); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="space-y-4" data-testid="partner-missions-admin">
      <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3 grid grid-cols-2 gap-2">
        <Input placeholder="Titre de la mission" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-2 bg-[#14101c] border-white/10 text-white" data-testid="mission-title" />
        <Input placeholder="Description / livrables attendus" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2 bg-[#14101c] border-white/10 text-white" data-testid="mission-desc" />
        <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="mission-due" />
        <div className="flex gap-2"><Input type="number" placeholder="Montant HT" value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="mission-amount" /><Button onClick={create} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="mission-create"><Plus className="w-4 h-4" /></Button></div>
      </div>
      {!missions ? <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /> : missions.length === 0 ? <Empty text="Aucune mission confiée." /> : missions.map((m) => (
        <div key={m.mission_id} className="bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2.5 text-xs" data-testid={"mission-row-" + m.mission_id}>
          <div className="flex items-center gap-2"><span className="font-bold text-white flex-1">{m.title}</span>{m.due_date && <span className="text-slate-500">{m.due_date}</span>}{m.amount_ht > 0 && <span className="text-slate-400">{m.amount_ht} € HT</span>}
            <select value={m.status} onChange={(e) => setStatus(m, e.target.value)} className="bg-[#14101c] border border-white/10 rounded px-2 py-1 text-[11px] text-white" data-testid={"mission-status-" + m.mission_id}>{Object.entries(MISSION_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          {m.description && <div className="text-slate-500 mt-1">{m.description}</div>}
          {m.partner_comment && <div className="text-sky-300 mt-1">Partenaire : {m.partner_comment}</div>}
        </div>
      ))}
    </div>
  );
}

function LeadsTab({ api, partner }) {
  const [leads, reloadLeads] = useList(api, `/partners/${partner.partner_id}/leads`, [partner.partner_id]);
  const [coms, reloadComs] = useList(api, `/partners/${partner.partner_id}/commissions`, [partner.partner_id]);
  const [form, setForm] = useState({ label: "", amount: "", lead_id: "" });
  const setLead = async (l, patch) => { try { await api.put(`/partners/${partner.partner_id}/leads/${l.lead_id}`, patch); reloadLeads(); } catch (e) { toast.error(e.message); } };
  const addCom = async () => {
    if (!form.amount) { toast.error("Montant requis"); return; }
    try { await api.post(`/partners/${partner.partner_id}/commissions`, form); setForm({ label: "", amount: "", lead_id: "" }); reloadComs(); toast.success("Commission ajoutée"); } catch (e) { toast.error(e.message); }
  };
  const pay = async (c) => { try { await api.put(`/partners/${partner.partner_id}/commissions/${c.commission_id}`, { status: "payee", paid_at: new Date().toISOString() }); reloadComs(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="space-y-5" data-testid="partner-leads-admin">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Leads apportés</div>
        {!leads ? <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /> : leads.length === 0 ? <Empty text="Aucun lead déclaré par ce partenaire." /> : leads.map((l) => (
          <div key={l.lead_id} className="bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2.5 text-xs mb-2" data-testid={"lead-row-" + l.lead_id}>
            <div className="flex items-center gap-2"><span className="font-bold text-white flex-1">{l.company || l.contact_name}</span><span className="text-slate-500">{l.contact_name} · {l.email}</span>{l.estimated_amount > 0 && <span className="text-slate-400">~{l.estimated_amount} €</span>}
              <select value={l.status} onChange={(e) => setLead(l, { status: e.target.value })} className="bg-[#14101c] border border-white/10 rounded px-2 py-1 text-[11px] text-white" data-testid={"lead-status-" + l.lead_id}>{LEAD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            {l.need && <div className="text-slate-500 mt-1">{l.need}</div>}
          </div>
        ))}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Commissions</div>
        <div className="flex gap-2 mb-2">
          <Input placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="commission-label" />
          <Input type="number" placeholder="Montant €" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#14101c] border-white/10 text-white w-32" data-testid="commission-amount" />
          <Button onClick={addCom} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="commission-create"><Plus className="w-4 h-4" /></Button>
        </div>
        {(coms || []).map((c) => (
          <div key={c.commission_id} className="flex items-center gap-2 bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 text-xs mb-1.5" data-testid={"commission-row-" + c.commission_id}>
            <span className="flex-1 text-white">{c.label}</span><span className="text-slate-300">{c.amount} €</span>
            <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (c.status === "payee" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400")}>{c.status === "payee" ? "Payée" : "En attente"}</span>
            {c.status !== "payee" && <Button size="sm" variant="ghost" onClick={() => pay(c)} className="text-emerald-300 text-[11px] h-7" data-testid={"commission-pay-" + c.commission_id}>Marquer payée</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MessagesThread({ api, listUrl, postUrl, me = "staff", testId = "partner-messages" }) {
  const [msgs, reload] = useList(api, listUrl, [listUrl]);
  const [body, setBody] = useState("");
  const send = async () => {
    if (!body.trim()) return;
    try { await api.post(postUrl, { body }); setBody(""); reload(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div data-testid={testId}>
      <div className="space-y-2 max-h-80 overflow-y-auto mb-3 pr-1">
        {!msgs ? <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /> : msgs.length === 0 ? <Empty text="Aucun message." /> : msgs.map((m) => (
          <div key={m.message_id} className={"max-w-[85%] rounded-xl px-3 py-2 text-xs " + (m.from === me ? "ml-auto bg-[#f7c935]/15 text-white" : "bg-white/5 text-slate-200")}>
            <div className="text-[10px] text-slate-500 mb-0.5">{m.author_name} · {new Date(m.created_at).toLocaleString("fr-FR")}</div>{m.body}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrire un message..." className="bg-[#14101c] border-white/10 text-white" data-testid={testId + "-input"} />
        <Button onClick={send} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid={testId + "-send"}><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function MessagesTab({ api, partner }) {
  return <MessagesThread api={api} listUrl={`/partners/${partner.partner_id}/messages`} postUrl={`/partners/${partner.partner_id}/messages`} me="staff" testId="partner-messages-admin" />;
}

export function DocumentsList({ api, listUrl, uploadUrl, testId = "partner-documents" }) {
  const [docs, reload] = useList(api, listUrl, [listUrl]);
  const ref = useRef(null);
  const [up, setUp] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); setUp(true);
    try { await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } }); reload(); toast.success("Document déposé"); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setUp(false); if (ref.current) ref.current.value = "";
  };
  const download = async (d) => {
    try {
      const r = await api.get(`/partners/documents/${d.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = d.filename || "document"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible"); }
  };
  return (
    <div data-testid={testId}>
      <input ref={ref} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={testId + "-input"} />
      <Button size="sm" variant="outline" disabled={up} onClick={() => ref.current?.click()} className="border-white/10 text-slate-200 text-xs mb-3" data-testid={testId + "-upload"}>{up ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Déposer un document</Button>
      {!docs ? <Loader2 className="w-5 h-5 animate-spin text-[#f7c935]" /> : docs.length === 0 ? <Empty text="Aucun document partagé." /> : docs.map((d) => (
        <div key={d.doc_id} className="flex items-center gap-2 bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 text-xs mb-1.5" data-testid={"doc-row-" + d.doc_id}>
          <span className="flex-1 text-white truncate">{d.filename}</span><span className="text-slate-500">{Math.round((d.size || 0) / 1024)} Ko · {d.from === "partner" ? "Partenaire" : "Équipe"}</span>
          <Button size="sm" variant="ghost" onClick={() => download(d)} className="text-slate-300 h-7" data-testid={"doc-download-" + d.doc_id}><Download className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ api, partner }) {
  return <DocumentsList api={api} listUrl={`/partners/${partner.partner_id}/documents`} uploadUrl={`/partners/${partner.partner_id}/documents`} testId="partner-documents-admin" />;
}

function Empty({ text }) {
  return <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-white/10 rounded-lg">{text}</div>;
}

